import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AttachmentTemplateForm } from '@/app/(client)/orders/-components/AttachmentTemplateForm';
import { useAttachmentTemplateDocumentTypes } from '@/app/(client)/orders/-hooks/useAttachmentTemplateDocumentTypes';
import type { AttachmentTemplateFormValues } from '@/app/(client)/orders/-types';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { useSaveAttachmentTemplate } from '../../-hooks/useSaveAttachmentTemplate';

export const Route = createFileRoute(
  '/management/attachment-templates/(pages)/create/',
)({
  component: CreateAttachmentTemplatePage,
});

function CreateAttachmentTemplatePage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementAttachmentTemplates',
  });
  const navigate = useNavigate();
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const { data: documentTypeOptions = [] } =
    useAttachmentTemplateDocumentTypes();

  const allFpKeys = (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
    g.options.map(o => o.key),
  );

  const { save, isPending } = useSaveAttachmentTemplate({
    allCountryCodes: countryOptions.map(c => c.key),
    // The backup rule's "all remaining" scope is derived from these at save
    // time; without them it would be persisted with no facility processes and
    // could never match an order.
    allFacilityProcessKeys: allFpKeys,
    // New templates start active — the server's own default for the field.
    isActive: true,
  });

  // No `hasOrders` here on purpose. The flag only comes from `/:id/details`,
  // which create never fetches, and a template that does not exist yet cannot
  // be referenced by an order. Wiring a guard in would disable Save forever.
  const handleSubmit = async (values: AttachmentTemplateFormValues) => {
    const result = await save(values);
    if (!result) return;
    void navigate({
      to: '/management/attachment-templates/$id',
      params: { id: result.id },
    });
  };

  return (
    <AttachmentTemplateForm
      onSubmit={handleSubmit}
      isPending={isPending}
      submitLabel={t('create.title')}
      fpGroups={fpGroups as FacilityProcessGroupOption[]}
      countryOptions={countryOptions}
      documentTypeOptions={documentTypeOptions}
    />
  );
}
